<template>
  <q-drawer
    ref="drawer"
    content-class="bg-dark"
    dark
    no-swipe-open
    overlay
    persistent
    side="right"
    @hide="
      setMainNavOpen(false);
      removeOutsideClickListener();
    "
    @show="addOutsideClickListener"
  >
    <q-list class="nav-list relative" @click="onListClick">
      <q-expansion-item
        v-if="isAuthenticated"
        :caption="currentUser.email"
        class="zubie-font text-white text-weight-bold"
        dark
        data-t="header"
        :default-opened="false"
        :disable="otherAccounts.length === 0"
        :hide-expand-icon="otherAccounts.length === 0"
        :label="account.nickname"
      >
        <MainNavigationItem
          v-for="account in otherAccounts"
          :key="account.id"
          data-t="accountsListLink"
          icon="switch_account"
          :label="account.nickname"
          @click.stop="switchAccount(account.id)"
        />
      </q-expansion-item>

      <q-separator color="white" />

      <MainNavigationItem
        v-if="isAuthenticated && canAccessRoute('/')"
        data-t="liveMapLink"
        icon="near_me"
        label="Live Map"
        to="/"
      />

      <MainNavigationItem
        v-if="isAuthenticated && canAccessRoute('/notifications') && $q.screen.xs"
        data-t="notificationsLink"
        icon="notifications"
        label="Notifications"
        to="/notifications"
      >
        <template #icon>
          <q-icon class="q-mr-sm" name="notifications" size="1.5rem">
            <NotificationsBadge v-if="areRecentUnread && !$q.screen.gt.xs" size="6px" />
          </q-icon>
        </template>
      </MainNavigationItem>

      <!-- Driver w/ Access Dashboard -->
      <MainNavigationItem
        v-if="isAuthenticated && isIndividual"
        data-t="dashboardLink"
        icon="bar_chart"
        label="Dashboard"
        to="/reports/dashboard/106"
      />

      <!-- Admin & Viewers Dashboard -->
      <MainNavigationItem
        v-if="isAuthenticated && !isIndividual && canAccessRoute('/dashboard') && (isFleet || isRental)"
        data-t="dashboardLink"
        :href="isFleet ? '/reports?report=/dashboard/11' : '/reports?report=/dashboard/33'"
        icon="bar_chart"
        label="Dashboard"
      />

      <MainNavigationItem
        v-if="isAuthenticated && canAccessRoute('/reports')"
        data-t="reportsLink"
        label="Reports"
        to="/reports"
      >
        <template #icon>
          <q-icon class="q-mr-sm rotate-270" name="note" size="1.5rem" />
        </template>
      </MainNavigationItem>

      <!-- Account with location devices -->
      <MainNavigationItem v-if="isAuthenticated" data-t="activityLink" label="Activity" to="/activity">
        <template #icon>
          <EventsFeedIcon class="q-mr-sm" size="1.5rem" />
        </template>
      </MainNavigationItem>

      <SmartMaintenanceLink v-if="isAuthenticated && isAllowedCarAdvise" />

      <MainNavigationItem
        v-if="isAuthenticated && canAccessRoute('/asset-recovery')"
        data-t="assetRecoveryLink"
        icon="input"
        label="Asset Recovery"
        to="/asset-recovery"
      />

      <MainNavigationItem
        v-if="isFleet && hasPermission('isAllowedToCheckIn')"
        data-t="checkInLink"
        icon="how_to_reg"
        label="Check In"
        to="/check-in"
      />

      <MainNavigationItem v-if="isAuthenticated && isIndividual" icon="directions_car" label="Feed" to="/feed">
        <template #icon>
          <EventsFeedIcon class="q-mr-sm" size="1.5rem" />
        </template>
      </MainNavigationItem>

      <q-separator v-if="isAuthenticated && !currentUser.isIndividual()" color="white" data-t="vehiclesSeparator" />

      <q-expansion-item
        v-if="isAuthenticated && !currentUser.isIndividual()"
        class="zubie-font text-white text-weight-bold"
        data-t="vehiclesListItem"
        default-opened
        label="Fleet Data"
        @click.stop
      >
        <MainNavigationItem
          v-if="canAccessRoute('/vehicles-assets')"
          data-t="vehiclesListLink"
          icon="directions_car"
          label="Vehicles &amp; Assets"
          :to="{ name: 'vehiclesAssets' }"
        />

        <MainNavigationItem
          v-if="canAccessRoute('/devices')"
          data-t="devicesLink"
          icon="developer_board"
          label="Devices"
          to="/devices"
        />

        <MainNavigationItem
          v-if="canAccessRoute('/places')"
          data-t="placesLink"
          icon="place"
          label="Places"
          to="/places"
        />

        <MainNavigationItem
          v-if="canAccessRoute('/users')"
          data-t="usersLink"
          icon="people"
          label="Users"
          to="/users"
        />

        <MainNavigationItem
          v-if="canAccessRoute('/shares')"
          data-t="sharesLink"
          icon="share"
          label="Shares"
          to="/shares"
        />
      </q-expansion-item>

      <q-separator v-if="isAuthenticated && !currentUser.isIndividual()" color="white" data-t="settingsSeparator" />

      <q-expansion-item
        v-if="isAuthenticated && !currentUser.isIndividual()"
        class="zubie-font text-white text-weight-bold"
        data-t="settingsItem"
        default-opened
        label="Settings"
        @click.stop
      >
        <MainNavigationItem
          v-if="canAccessRoute('/users')"
          data-t="myProfileLink"
          icon="person"
          label="My Profile"
          to="/users/me"
        />

        <MainNavigationItem
          v-if="canAccessRoute('/subscriptions') && hasPermission('isAccountAdmin')"
          data-t="billingLink"
          icon="attach_money"
          label="Billing &amp; Subscriptions"
          to="/subscriptions"
        />

        <MainNavigationItem
          v-if="hasPermission('accountView') && hasPermission('isAccountAdmin')"
          data-t="preferencesLink"
          icon="settings"
          label="Account Preferences"
          @click="openAccountPreferences"
        />

        <MainNavigationItem
          v-if="canAccessRoute('/apps')"
          data-t="preferencesLink"
          icon="widgets"
          label="Apps"
          to="/apps"
        />

        <MainNavigationItem
          v-if="canAccessRoute('/groups')"
          data-t="groupsLink"
          icon="supervised_user_circle"
          label="Groups"
          to="/groups"
        />

        <MainNavigationItem v-if="canAccessRoute('/tags')" data-t="groupsLink" icon="label" label="Tags" to="/tags" />

        <MainNavigationItem
          v-if="canAccessRoute('/asset-types')"
          data-t="groupsLink"
          icon="supervised_user_circle"
          label="Asset Types"
          to="/asset-types"
        >
          <template #icon>
            <q-icon class="q-mr-sm" :name="assetsIcon" size="1.5rem" />
          </template>
        </MainNavigationItem>
      </q-expansion-item>

      <q-separator v-if="isAuthenticated" color="white" data-t="helpSeparator" />

      <MainNavigationItem
        v-if="isAtMobileTitleSize"
        class="intercom-trigger"
        data-t="intercomLink"
        icon="support_agent"
        label="Get Help"
      />

      <MainNavigationItem
        v-if="isAtMobileTitleSize"
        data-t="helpLink"
        icon="help"
        label="Help Center"
        @click.native="openURL('https://help.zubie.com/en/')"
      />

      <MainNavigationItem icon="description" label="Release Notes" to="/release-notes" />

      <MainNavigationItem icon="gavel" label="Attributions" to="/attributions" />

      <MainNavigationItem v-if="!isAuthenticated" data-t="loginLink" icon="how_to_reg" label="Login" to="/login" />

      <MainNavigationItem
        v-if="isAuthenticated"
        data-t="logoutLink"
        icon="logout"
        label="Logout"
        @click.native="logout()"
      />

      <MainNavigationItem
        class="absolute-bottom-right no-pointer-events column min-height-none q-mb-xs text-weight-light text-right"
        :clickable="false"
        @click.stop
      >
        <div>v.{{ version }}</div>
        <div v-if="appBuild">b.{{ appBuild }}</div>
      </MainNavigationItem>
    </q-list>
  </q-drawer>
</template>

<script setup lang="ts">
import { Dialog, openURL, QList, useQuasar } from 'quasar';
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import EventsFeedIcon from 'components/icons/EventsFeedIcon.vue';
import NotificationsBadge from 'components/notifications/NotificationsBadge.vue';
import SmartMaintenanceLink from 'components/SmartMaintenanceLink.vue';
import { useOutsideClick } from 'src/composables/outside-click';
import { usePageTitle } from 'src/composables/page-title';
import { ASSET_ICONS, ASSET_TYPES } from 'src/services/constants';
import { canAccessRoute as canAccessRouteService } from 'src/services/router';
import AccountPreferencesDialog from './account/AccountPreferencesDialog.vue';
import MainNavigationItem from './MainNavigationItem.vue';

defineOptions({ name: 'MainNavigationDrawer' });

const store = useStore();
const $q = useQuasar();
const { addOutsideClickListener, removeOutsideClickListener } = useOutsideClick(
  'mainNavDrawer',
  () => {
    if (mainNavOpen.value) {
      void setMainNavOpen(false);
    }
  },
  false
);

const drawer = ref<{ show: () => void; hide: () => void } | null>(null);

const account = computed(() => store.getters['session/account']);
const accounts = computed(() => store.state.session.accounts || []);
const currentUser = computed(() => store.getters['session/currentUser']);
const hasPermission = computed(() => store.getters['session/hasPermission']);
const isAuthenticated = computed(() => store.getters['session/isAuthenticated']);
const isFleet = computed(() => store.getters['session/isFleet']);
const isRental = computed(() => store.getters['session/isRental']);
const areRecentUnread = computed(() => store.getters['notifications/areRecentUnread']);
const deviceInfo = computed(() => store.state.app.deviceInfo);
const mainNavOpen = computed(() => store.state.app.mainNavOpen);
const carAdviseEnabledAccountIds = computed(
  () => (store.state.env.carAdviseEnabledAccountIds as Array<string | number>) || []
);

const { isAtMobileTitleSize } = usePageTitle();

const assetsIcon = computed(() => ASSET_ICONS[ASSET_TYPES.ASSET]);
const appBuild = computed(() => deviceInfo.value?.appBuild);
const isIndividual = computed(() => currentUser.value?.isIndividual?.() ?? false);
const isAllowedCarAdvise = computed(() => {
  const currentAccountId = (account.value as { id?: string | number } | undefined)?.id;
  const permissionFn = hasPermission.value as ((permission: string) => boolean) | undefined;
  return (
    (currentAccountId != null && carAdviseEnabledAccountIds.value.includes(currentAccountId)) ||
    Boolean(permissionFn?.('smartMaintenanceView')) ||
    Boolean(permissionFn?.('smartMaintenancePreview'))
  );
});
const version = computed(() => process.env.APP_VERSION);

const otherAccounts = computed(() => {
  const currentAccountId = account.value?.id;
  return (accounts.value || [])
    .filter(({ id }) => id !== currentAccountId)
    .sort(({ nickname: n1 = '' }, { nickname: n2 = '' }) => {
      const first = n1.toLowerCase();
      const second = n2.toLowerCase();
      return first <= second ? -1 : 1;
    });
});

const setMainNavOpen = (value: boolean) => store.dispatch('app/setMainNavOpen', value);
const logout = () => store.dispatch('app/logout');
const switchAccount = (accountId: string | number) => store.dispatch('app/switchAccount', String(accountId));

const canAccessRoute = (path: string) => canAccessRouteService(path);

function openAccountPreferences() {
  Dialog.create({
    component: AccountPreferencesDialog,
  });
}

watch(
  mainNavOpen,
  (isOpen) => {
    if (isOpen) {
      drawer.value?.show();
    } else {
      drawer.value?.hide();
    }
  },
  { immediate: true }
);

function onListClick(event) {
  let target = event.target as HTMLElement;

  // Check if what was clicked was within a navigation item (stop at list parent)
  let isNavigationItem = false;
  while (target && !target.classList.contains('nav-list')) {
    if (target.getAttribute('aria-expanded')) {
      break; // not a link
    }
    if (target.classList.contains('q-link')) {
      isNavigationItem = true;
      break;
    }
    target = target.parentNode as HTMLElement;
  }

  if (!isNavigationItem) {
    // Prevent "outside click" if a non-navigation item was clicked
    event.stopPropagation();
  }
}
</script>

<style lang="scss" scoped>
.q-expansion-item :deep([role='button'] .q-item__label) {
  font-size: 1.3rem;
}

.q-expansion-item :deep([role='button'] .q-item__label--caption) {
  font-size: 0.9rem;
}

.q-expansion-item.text-white :deep(.q-item.disabled) {
  opacity: 100% !important;
}

.q-link {
  align-items: center;
  text-decoration: none;
}

.q-link:hover,
.q-link:active {
  background: $dark;
}

.q-item__label--header {
  font-size: 1.3rem;
}

.q-item__label--caption {
  font-size: 1em;
}

:deep(.text-white .q-item__section--side) {
  color: $white;
}

:deep(.notification-badge) {
  bottom: 13px;
  left: 7px;
}
</style>
